import type { LucideProps } from '@lucide/vue'
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import type { FunctionalComponent } from 'vue'

interface Action {
  label: string
  onClick: () => void
  icon?: FunctionalComponent<LucideProps>
}

interface ErrorState {
  hasError: boolean
  type: '401' | '403' | '404' | 'custom' | null
  title: string
  message: () => string
  icon: FunctionalComponent<LucideProps> | null
  primaryAction: Action | null
  secondaryAction: Action | null
}

export const useErrorStore = defineStore('error', () => {
  const hasError = ref(false)
  const type = ref<ErrorState['type']>(null)
  const title = ref('')
  const presentation = shallowRef<
    Pick<ErrorState, 'message' | 'icon' | 'primaryAction' | 'secondaryAction'>
  >({
    message: () => '',
    icon: null,
    primaryAction: null,
    secondaryAction: null
  })

  const message = (): string => presentation.value.message()

  const setError = (payload: Partial<ErrorState>): void => {
    hasError.value = true
    type.value = payload.type ?? 'custom'
    title.value = payload.title ?? ''
    presentation.value = {
      message: payload.message ?? ((): string => ''),
      icon: payload.icon ?? null,
      primaryAction: payload.primaryAction ?? null,
      secondaryAction: payload.secondaryAction ?? null
    }
  }

  const clearError = (): void => {
    hasError.value = false
    type.value = null
    title.value = ''
    presentation.value = {
      message: () => '',
      icon: null,
      primaryAction: null,
      secondaryAction: null
    }
  }

  return {
    $reset: clearError,
    clearError,
    hasError,
    icon: computed(() => presentation.value.icon),
    message,
    primaryAction: computed(() => presentation.value.primaryAction),
    secondaryAction: computed(() => presentation.value.secondaryAction),
    setError,
    title,
    type
  }
})
